import React, {FC, useRef, useState} from "react";
import {
  Button,
  CardMedia,
  IconButton,
  Typography,
  useTheme,
} from "@material-ui/core";
import UploadIcon from "view/images/UploadIcon";
import DeleteIcon from "@material-ui/icons/Delete";
import FlexBox from "view/components/Layout/FlexBox";
import bytesToMegaBytes from "tools/file/bytesToMegaBytes";
import {RawAttachmentFile} from "ts/api/attachmentFile";
import {v4 as uuidv4} from "uuid";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import {Alert} from "@material-ui/lab";
import clsx from "clsx";
import {ISignature} from "@evennt-tech/api-v2-core";
import useStyles from "./styles";
import FilePreview from "../FilePreview";

type Props = {
  uploadedFile: RawAttachmentFile | null;
  setUploadedFile: (file: RawAttachmentFile | null) => void;
  signatureToEdit?: ISignature;
  showCanvas: boolean;
  setShowCanvas: React.Dispatch<React.SetStateAction<boolean>>;
  style?: React.CSSProperties;
};

const fileFormats = ".pdf,.jpeg,.png";
const supportedTypes = ["image/jpeg", "image/png", "application/pdf"];
const fileSizeLimit = 3; // MB

const FileDisplay: FC<{file: File; classes: any}> = ({file, classes}) => {
  if (file.type !== "application/pdf") {
    return (
      <CardMedia
        component="img"
        image={URL.createObjectURL(file)}
        alt="Signature preview"
        className={classes.fileImage}
      />
    );
  }

  return <PictureAsPdfIcon className={classes.fileImage} />;
};

const UploadSignature: FC<Props> = ({
  uploadedFile,
  setUploadedFile,
  signatureToEdit,
  showCanvas,
  setShowCanvas,
  style,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);

  const handleSaveFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      const size = bytesToMegaBytes(file.size || 0);

      if (size > fileSizeLimit) {
        setErrorMessage(
          `File is too large. Maximum size is ${fileSizeLimit} MB.`
        );
        return;
      }

      const fileType = file.type.toLowerCase();
      if (!supportedTypes.includes(fileType)) {
        setErrorMessage(
          `Unsupported file type. Allowed formats are ${fileFormats.replace(
            /,/g,
            ", "
          )}.`
        );
        return;
      }

      const attachmentFile: RawAttachmentFile = {
        id: uuidv4(),
        title: file.name,
        file,
      };

      setUploadedFile(attachmentFile);
      setErrorMessage(null);
    }
  };
  const handleRemoveFile = () => {
    setUploadedFile(null);
    setShowCanvas(true);
    setErrorMessage(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };
  const handleDropFile = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);

    if (event.dataTransfer.files && event.dataTransfer.files[0]) {
      const dataTransferFile = event.dataTransfer.files[0];
      const fileInputEvent = ({
        target: {files: [dataTransferFile]},
      } as unknown) as React.ChangeEvent<HTMLInputElement>;
      handleSaveFile(fileInputEvent);
    }
  };
  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(true);
  };
  const handleDragLeave = () => {
    setDragging(false);
  };

  if (signatureToEdit?.file && !showCanvas) {
    return (
      <FilePreview
        file={signatureToEdit.file}
        handleRemoveFile={handleRemoveFile}
      />
    );
  }

  return (
    <>
      {uploadedFile && (
        <FlexBox alignCenter className={classes.filePreview}>
          <FlexBox alignCenter>
            <FileDisplay file={uploadedFile.file} classes={classes} />
            <Typography className={classes.fileName}>
              {uploadedFile.title}
            </Typography>
          </FlexBox>
          <IconButton onClick={handleRemoveFile}>
            <DeleteIcon />
          </IconButton>
        </FlexBox>
      )}
      <FlexBox
        column
        alignCenter
        justifyCenter
        className={clsx(classes.uploadContainer, {
          [classes.dragging]: dragging,
        })}
        style={{
          backgroundColor: style?.backgroundColor
            ? style.backgroundColor
            : "white",
        }}
        onDragOver={handleDragOver}
        onDrop={handleDropFile}
        onDragLeave={handleDragLeave}
      >
        <Typography className={classes.uploadText}>
          Please drag and drop or upload file here
          <br />
          {`(< ${fileSizeLimit} MB)`}
        </Typography>
        <input
          type="file"
          id="file-upload"
          className={classes.fileInput}
          accept={fileFormats}
          ref={fileInputRef}
          onChange={handleSaveFile}
        />
        <Button
          variant="contained"
          startIcon={<UploadIcon color={theme.palette.primary.main} />}
          className={classes.uploadButton}
          onClick={() => fileInputRef.current?.click()}
        >
          Upload
        </Button>
      </FlexBox>
      {errorMessage && (
        <Alert severity="error" className={classes.infoBlock}>
          {errorMessage}
        </Alert>
      )}
    </>
  );
};

export default UploadSignature;
